import { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import {
  Search,
  Loader2,
  MapPin,
  Lightbulb,
  Clock,
  ChevronDown,
  ChevronUp,
  Database,
  Zap,
  AlertCircle,
} from 'lucide-react'
import { useLocation } from '../../contexts/LocationContext'
import { useData } from '../../contexts/DataContext'
import { useConfig } from '../../contexts/ConfigContext'
import { getExampleQueries } from '../../utils/mockQueryHandler'
import { AI_API_ENDPOINTS } from '../../config/api'
import CacheQueryCard from './CacheQueryCard'

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    fontSize: '14px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': {
      borderColor: '#9ca3af',
    },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isSelected
      ? '#3b82f6'
      : state.isFocused
        ? '#dbeafe'
        : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
  }),
}

/**
 * AiQueryContext Component
 *
 * Comprehensive query interface with:
 * - Location & category selectors
 * - Natural language query input
 * - Integrated Examples + Recent Queries (toggleable)
 * - Collapsible cache statistics
 * - Cache-aware query submission
 * - Duplicate detection with smart caching
 */
export default function AiQueryContext({
  onQuerySubmit,
  isProcessing,
  setIsProcessing,
  onCacheClick,
}) {
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('health')
  const inputRef = useRef(null)

  // Location context - use global state for consistency
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    setDistrict,
    setSubcounty,
    setParish,
    setVillage,
    getLocationCodeById,
  } = useLocation()
  const { locations } = useData()
  const { getLocationDepthOrder } = useConfig()

  // Cache browser state
  const [viewMode, setViewMode] = useState('examples') // 'examples' or 'recent'
  const [recentQueries, setRecentQueries] = useState([])
  const [loadingRecent, setLoadingRecent] = useState(false)
  const [cacheStats, setCacheStats] = useState(null)
  const [showCacheStats, setShowCacheStats] = useState(false)

  // Cache-aware submission state
  const [duplicateCheck, setDuplicateCheck] = useState(null)
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false)

  // Get available locations based on selections
  const availableDistricts = useMemo(() => {
    if (!locations?.districts) return []
    return [...locations.districts].sort((a, b) => a.name.localeCompare(b.name))
  }, [locations])

  const availableSubcounties = useMemo(() => {
    if (!selectedDistrict) return []
    const district = availableDistricts.find(d => d.id === selectedDistrict)
    return (district?.subcounties || []).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [selectedDistrict, availableDistricts])

  const availableParishes = useMemo(() => {
    if (!selectedSubcounty) return []
    const subcounty = availableSubcounties.find(s => s.id === selectedSubcounty)
    return (subcounty?.parishes || []).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [selectedSubcounty, availableSubcounties])

  const availableVillages = useMemo(() => {
    if (!selectedParish) return []
    const parish = availableParishes.find(p => p.id === selectedParish)
    return (parish?.villages || []).sort((a, b) => a.name.localeCompare(b.name))
  }, [selectedParish, availableParishes])

  // Restore context from localStorage on mount
  useEffect(() => {
    try {
      const savedContext = localStorage.getItem('aiQueryContext')
      if (savedContext) {
        const parsed = JSON.parse(savedContext)
        if (parsed.query) setQuery(parsed.query)
        if (parsed.category) setCategory(parsed.category)
        if (parsed.selectedDistrict) setDistrict(parsed.selectedDistrict)
        if (parsed.selectedSubcounty) setSubcounty(parsed.selectedSubcounty)
        if (parsed.selectedParish) setParish(parsed.selectedParish)
        if (parsed.selectedVillage) setVillage(parsed.selectedVillage)
      }
    } catch (err) {
      console.error('Failed to restore query context:', err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Save context to localStorage whenever it changes
  useEffect(() => {
    try {
      const contextToSave = {
        query,
        category,
        selectedDistrict,
        selectedSubcounty,
        selectedParish,
        selectedVillage,
      }
      localStorage.setItem('aiQueryContext', JSON.stringify(contextToSave))
    } catch (err) {
      console.error('Failed to save query context:', err)
    }
  }, [
    query,
    category,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
  ])

  // Load cache stats on mount
  useEffect(() => {
    loadCacheStats()
  }, [])

  // Load recent queries when view changes or filters change
  useEffect(() => {
    if (viewMode === 'recent') {
      loadRecentQueries()
    }
  }, [viewMode, category, selectedDistrict, selectedSubcounty, selectedParish])

  // Load from URL on mount (URL params override localStorage)
  useEffect(() => {
    const urlQuery = searchParams.get('query')
    const urlCategory = searchParams.get('category')
    const urlDistrict = searchParams.get('district')

    // URL parameters take priority over localStorage
    if (urlQuery) {
      setQuery(urlQuery)
    }
    if (urlCategory && ['health', 'education'].includes(urlCategory)) {
      setCategory(urlCategory)
    }
    if (urlDistrict) {
      // Find district by code
      const district = availableDistricts.find(
        d => getLocationCodeById(d.id) === urlDistrict
      )
      if (district) {
        setDistrict(district.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, availableDistricts, getLocationCodeById])

  const loadCacheStats = async () => {
    try {
      const response = await fetch(AI_API_ENDPOINTS.cacheStats)
      if (response.ok) {
        const data = await response.json()
        setCacheStats(data)
      }
    } catch (err) {
      console.error('Failed to load cache stats:', err)
    }
  }

  const loadRecentQueries = async () => {
    setLoadingRecent(true)

    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '10',
        category,
      })

      // Add location filters if selected
      if (selectedDistrict) {
        params.set('district', getLocationCodeById(selectedDistrict))
      }
      if (selectedSubcounty) {
        params.set('subcounty', getLocationCodeById(selectedSubcounty))
      }
      if (selectedParish) {
        params.set('parish', getLocationCodeById(selectedParish))
      }

      const response = await fetch(
        `${AI_API_ENDPOINTS.cachedQueries}?${params}`
      )

      if (response.ok) {
        const data = await response.json()
        setRecentQueries(data.queries || [])
      }
    } catch (err) {
      console.error('Error loading recent queries:', err)
    } finally {
      setLoadingRecent(false)
    }
  }

  const checkForDuplicate = async () => {
    if (!query.trim() || !selectedDistrict) return

    try {
      const location = {
        district: getLocationCodeById(selectedDistrict),
      }
      if (selectedSubcounty) {
        location.subcounty = getLocationCodeById(selectedSubcounty)
      }
      if (selectedParish) {
        location.parish = getLocationCodeById(selectedParish)
      }
      if (selectedVillage) {
        location.village = getLocationCodeById(selectedVillage)
      }

      const response = await fetch(AI_API_ENDPOINTS.checkDuplicate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query.trim(),
          location,
          category,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setDuplicateCheck(data)

        // If duplicate and recent (< 1 hour), show warning
        if (data.isDuplicate && data.cacheAge < 3600) {
          setShowDuplicateWarning(true)
          return true
        }
      }
    } catch (err) {
      console.error('Error checking for duplicate:', err)
    }

    return false
  }

  const handleSubmit = async (e, forceRefresh = false) => {
    e?.preventDefault()

    if (!query.trim() || isProcessing) return

    // Validate query length
    if (query.length < 3 || query.length > 500) {
      alert('Query must be between 3 and 500 characters')
      return
    }

    // Require district selection
    if (!selectedDistrict) {
      alert('Please select a district before submitting your query')
      return
    }

    // Check for duplicate if not forcing refresh
    if (!forceRefresh) {
      const isDuplicate = await checkForDuplicate()
      if (isDuplicate) {
        // Warning is shown, wait for user action
        return
      }
    }

    // Clear duplicate warning
    setShowDuplicateWarning(false)
    setDuplicateCheck(null)

    setIsProcessing(true)

    try {
      // Build location object with codes
      const location = {
        district: getLocationCodeById(selectedDistrict),
      }
      if (selectedSubcounty) {
        location.subcounty = getLocationCodeById(selectedSubcounty)
      }
      if (selectedParish) {
        location.parish = getLocationCodeById(selectedParish)
      }
      if (selectedVillage) {
        location.village = getLocationCodeById(selectedVillage)
      }

      // Make API call
      const response = await fetch(AI_API_ENDPOINTS.query, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query.trim(),
          location,
          category,
          forceRefresh,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Handle error responses
        if (response.status === 429) {
          onQuerySubmit({
            type: 'error',
            text: 'Rate limit exceeded. Please wait a moment and try again.',
            visualizations: [],
          })
        } else {
          onQuerySubmit(data)
        }
      } else {
        // Success - pass response to parent
        onQuerySubmit(data, {
          query: query.trim(),
          district: location.district,
          subcounty: location.subcounty,
          parish: location.parish,
          village: location.village,
          category,
        })

        // Reload recent queries after successful submission
        if (viewMode === 'recent') {
          setTimeout(loadRecentQueries, 500)
        }
      }
    } catch (error) {
      console.error('Query error:', error)
      onQuerySubmit({
        type: 'error',
        text: 'Failed to connect to the API. Please check your connection and try again.',
        visualizations: [],
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleUseCachedResult = async () => {
    if (!duplicateCheck?.cacheKey) return

    setShowDuplicateWarning(false)
    setIsProcessing(true)

    try {
      const response = await fetch(
        AI_API_ENDPOINTS.cachedQuery(duplicateCheck.cacheKey)
      )

      if (response.ok) {
        const data = await response.json()
        onQuerySubmit(
          {
            ...data.response,
            cached: true,
            _metadata: data.metadata,
          },
          {
            query: query.trim(),
            district: getLocationCodeById(selectedDistrict),
            subcounty: selectedSubcounty
              ? getLocationCodeById(selectedSubcounty)
              : undefined,
            parish: selectedParish
              ? getLocationCodeById(selectedParish)
              : undefined,
            village: selectedVillage
              ? getLocationCodeById(selectedVillage)
              : undefined,
            category,
          }
        )
      }
    } catch (err) {
      console.error('Error loading cached result:', err)
      onQuerySubmit({
        type: 'error',
        text: 'Failed to load cached result.',
        visualizations: [],
      })
    } finally {
      setIsProcessing(false)
      setDuplicateCheck(null)
    }
  }

  const handleCacheCardClick = async cacheKey => {
    // Load the cached query details
    setIsProcessing(true)

    try {
      const response = await fetch(AI_API_ENDPOINTS.cachedQuery(cacheKey))

      if (response.ok) {
        const data = await response.json()

        // Populate context from cached query metadata
        populateContextFromCache(data.metadata)

        // Pass to parent via onCacheClick
        onCacheClick(cacheKey)
      }
    } catch (err) {
      console.error('Error loading cached query:', err)
    } finally {
      setIsProcessing(false)
    }
  }

  const populateContextFromCache = metadata => {
    // Set query text
    setQuery(metadata.prompt)

    // Set category
    if (metadata.category) {
      setCategory(metadata.category)
    }

    // Set location selectors
    if (metadata.location?.district) {
      const district = availableDistricts.find(
        d => getLocationCodeById(d.id) === metadata.location.district
      )
      if (district) {
        setDistrict(district.id)

        // Wait for district selection to trigger subcounty update
        setTimeout(() => {
          if (metadata.location?.subcounty) {
            const districtData = locations.districts.find(
              d => d.id === district.id
            )
            const subcounty = districtData?.subcounties?.find(
              s => getLocationCodeById(s.id) === metadata.location.subcounty
            )
            if (subcounty) {
              setSubcounty(subcounty.id)

              // Continue with parish
              setTimeout(() => {
                if (metadata.location?.parish) {
                  const parish = subcounty?.parishes?.find(
                    p => getLocationCodeById(p.id) === metadata.location.parish
                  )
                  if (parish) {
                    setParish(parish.id)

                    // Continue with village
                    setTimeout(() => {
                      if (metadata.location?.village) {
                        const village = parish?.villages?.find(
                          v =>
                            getLocationCodeById(v.id) ===
                            metadata.location.village
                        )
                        if (village) {
                          setVillage(village.id)
                        }
                      }
                    }, 100)
                  }
                }
              }, 100)
            }
          }
        }, 100)
      }
    }
  }

  const handleExampleClick = example => {
    setQuery(example)
    // Auto-submit after setting query
    setTimeout(() => {
      if (selectedDistrict) {
        const fakeEvent = { preventDefault: () => {} }
        handleSubmit(fakeEvent)
      }
    }, 100)
  }

  const handleClear = () => {
    setQuery('')
    setShowDuplicateWarning(false)
    setDuplicateCheck(null)
    inputRef.current?.focus()
  }

  // Filter recent queries by query input text
  const filteredRecentQueries = useMemo(() => {
    if (!query.trim() || viewMode !== 'recent') {
      return recentQueries
    }

    const searchLower = query.toLowerCase()
    return recentQueries.filter(q =>
      q.prompt.toLowerCase().includes(searchLower)
    )
  }, [recentQueries, query, viewMode])

  // Get example queries for current category
  const exampleQueries = getExampleQueries(category)

  // Generate location options
  const districtOptions = availableDistricts.map(d => ({
    value: d.id,
    label: d.name,
  }))

  const subcountyOptions = availableSubcounties.map(s => ({
    value: s.id,
    label: s.name,
  }))

  const parishOptions = availableParishes.map(p => ({
    value: p.id,
    label: p.name,
  }))

  const villageOptions = availableVillages.map(v => ({
    value: v.id,
    label: v.name,
  }))

  // Format cache age
  const formatCacheAge = seconds => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
    return `${Math.floor(seconds / 86400)}d`
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Category Selector */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Query Context</h3>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCategory('health')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              category === 'health'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Health
          </button>
          <button
            type="button"
            onClick={() => setCategory('education')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              category === 'education'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Education
          </button>
        </div>
      </div>

      {/* Location Selectors */}
      <div className="mb-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* District */}
          {getLocationDepthOrder().includes('district') && (
            <div>
              <label
                className="block text-xs font-medium text-gray-700 mb-1"
                htmlFor="district"
              >
                District *
              </label>
              <Select
                value={districtOptions.find(d => d.value === selectedDistrict)}
                onChange={option => {
                  setDistrict(option?.value || null)
                }}
                options={districtOptions}
                styles={customSelectStyles}
                isClearable
                placeholder="Select district..."
                htmlFor="district"
              />
            </div>
          )}

          {/* Subcounty */}
          {getLocationDepthOrder().includes('subcounty') && (
            <div>
              <label
                className="block text-xs font-medium text-gray-700 mb-1"
                htmlFor="subcounty"
              >
                Subcounty
              </label>
              <Select
                value={subcountyOptions.find(
                  s => s.value === selectedSubcounty
                )}
                onChange={option => {
                  setSubcounty(option?.value || null)
                }}
                options={subcountyOptions}
                styles={customSelectStyles}
                isClearable
                isDisabled={!selectedDistrict}
                placeholder="Select subcounty..."
                htmlFor="subcounty"
              />
            </div>
          )}

          {/* Parish */}
          {getLocationDepthOrder().includes('parish') && (
            <div>
              <label
                className="block text-xs font-medium text-gray-700 mb-1"
                htmlFor="parish"
              >
                Parish
              </label>
              <Select
                value={parishOptions.find(p => p.value === selectedParish)}
                onChange={option => {
                  setParish(option?.value || null)
                }}
                options={parishOptions}
                styles={customSelectStyles}
                isClearable
                isDisabled={!selectedSubcounty}
                placeholder="Select parish..."
                htmlFor="parish"
              />
            </div>
          )}

          {/* Village */}
          {getLocationDepthOrder().includes('village') && (
            <div>
              <label
                className="block text-xs font-medium text-gray-700 mb-1"
                htmlFor="village"
              >
                Village
              </label>
              <Select
                value={villageOptions.find(v => v.value === selectedVillage)}
                onChange={option => setVillage(option?.value || null)}
                options={villageOptions}
                styles={customSelectStyles}
                isClearable
                isDisabled={!selectedParish}
                placeholder="Select village..."
                htmlFor="village"
              />
            </div>
          )}
        </div>
      </div>

      {/* Query Input */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={
              viewMode === 'recent'
                ? 'Search recent queries or ask a new question...'
                : 'Ask a question about your data...'
            }
            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400"
            disabled={isProcessing}
            minLength={3}
            maxLength={500}
          />
          <button
            type="submit"
            disabled={!query.trim() || isProcessing || !selectedDistrict}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
          </button>
        </div>

        {query && (
          <div className="text-xs text-gray-500 flex justify-between items-center">
            <span>{query.length} / 500 characters</span>
            <button
              type="button"
              onClick={handleClear}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear
            </button>
          </div>
        )}
      </form>

      {/* Duplicate Warning */}
      {showDuplicateWarning && duplicateCheck && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 mb-1">
                This query was cached {formatCacheAge(duplicateCheck.cacheAge)}{' '}
                ago
              </p>
              <p className="text-sm text-blue-800 mb-3">
                {duplicateCheck.summary}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleUseCachedResult}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Use Cached Result
                </button>
                <button
                  onClick={e => handleSubmit(e, true)}
                  className="px-3 py-1.5 bg-white text-blue-600 text-sm font-medium rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors"
                >
                  Re-run Query
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle: Examples vs Recent Queries */}
      {!isProcessing && !showDuplicateWarning && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setViewMode('examples')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'examples'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Lightbulb className="w-4 h-4" />
              Examples
            </button>
            <button
              onClick={() => setViewMode('recent')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'recent'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Clock className="w-4 h-4" />
              Recent Queries
              {selectedDistrict && recentQueries.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-white bg-opacity-30 rounded text-xs">
                  {filteredRecentQueries.length}
                </span>
              )}
            </button>
          </div>

          {/* Examples View */}
          {viewMode === 'examples' && (
            <div className="space-y-1.5">
              {exampleQueries.map((example, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(example)}
                  disabled={!selectedDistrict}
                  className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors group text-sm text-gray-700 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lightbulb className="w-3.5 h-3.5 inline-block mr-2 text-gray-400 group-hover:text-blue-600" />
                  {example}
                </button>
              ))}
              {!selectedDistrict && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Please select a district to use example queries
                </p>
              )}
            </div>
          )}

          {/* Recent Queries View */}
          {viewMode === 'recent' && (
            <div>
              {loadingRecent ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div
                      key={i}
                      className="animate-pulse bg-gray-100 rounded-lg h-16"
                    />
                  ))}
                </div>
              ) : filteredRecentQueries.length > 0 ? (
                <div className="space-y-2">
                  {filteredRecentQueries.map(q => (
                    <CacheQueryCard
                      key={q.cacheKey}
                      query={q}
                      onClick={() => handleCacheCardClick(q.cacheKey)}
                    />
                  ))}
                  {query.trim() &&
                    filteredRecentQueries.length < recentQueries.length && (
                      <p className="text-xs text-gray-500 text-center py-2">
                        Showing {filteredRecentQueries.length} of{' '}
                        {recentQueries.length} queries matching &quot;{query}
                        &quot;
                      </p>
                    )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {selectedDistrict
                      ? query.trim()
                        ? `No recent queries match "${query}"`
                        : 'No recent queries for this context'
                      : 'Select a district to see recent queries'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Collapsible Cache Stats */}
      {cacheStats && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <button
            onClick={() => setShowCacheStats(!showCacheStats)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-gray-600" />
              <span>Cache Statistics</span>
            </div>
            {showCacheStats ? (
              <ChevronUp className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            )}
          </button>

          {showCacheStats && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600 font-medium mb-1">
                  Total Queries
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {cacheStats.totalCachedQueries}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-green-600 font-medium mb-1">
                  Cache Hits
                </p>
                <p className="text-2xl font-bold text-green-900">
                  {cacheStats.totalCacheHits}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs text-purple-600 font-medium mb-1">
                  Hit Rate
                </p>
                <p className="text-2xl font-bold text-purple-900">
                  {cacheStats.cacheHitRate}
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-amber-600 font-medium mb-1">
                  Avg Usage
                </p>
                <p className="text-2xl font-bold text-amber-900">
                  {cacheStats.averageUsagePerQuery}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Processing Indicator */}
      {isProcessing && !showDuplicateWarning && (
        <div className="mt-4 flex items-center justify-center py-6">
          <div className="text-center space-y-3">
            <div className="inline-block p-3 bg-blue-50 rounded-full">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
            <p className="text-sm text-gray-600">Processing your query...</p>
          </div>
        </div>
      )}
    </div>
  )
}

AiQueryContext.propTypes = {
  onQuerySubmit: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  setIsProcessing: PropTypes.func.isRequired,
  onCacheClick: PropTypes.func.isRequired,
}
